import React, {useCallback, useEffect, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {useHistory} from 'react-router-dom'
import {useErrorHandling} from 'context/errorHandling'
import {deleteInsurancePolicy, fetchContractorPolicies, getInsurancePolicyFile} from 'services/InsurancePolicyService'
import styles from 'components/Contractors/InsurancePolicy/Details/InsurancePolicyList.module.css'
import IconButton from '@material-ui/core/IconButton'
import {ReactComponent as RemoveIcon} from 'assets/icons/trash.svg'
import {ReactComponent as EditIcon} from 'assets/icons/edit.svg'
import {ReactComponent as AddIcon} from 'assets/icons/add-red.svg'
import {ReactComponent as PreviewIcon} from 'assets/icons/copy.svg'
import moment from 'moment'
import {PrimaryButton} from 'components/Shared/PrimaryButton'
import {WarningDialog} from 'components/Shared/Dialogs'
import {FilesPreviewDialog} from 'components/Shared/FilesPreviewDialog'
import classNames from 'classnames'
import {
  CONTRACTOR_POLICY_CREATE, CONTRACTOR_POLICY_DELETE,
  CONTRACTOR_POLICY_EDIT,
  useAuth
} from 'context/auth';
import {fetchContractor} from "../../../../services/ContractorService";

export function InsurancePolicyList({contractorId}) {
  const [policies, setPolicies] = useState(null)
  const [insurers, setInsurers] = useState([])
  const [elementToRemove, setElementToRemove] = useState(null)
  const [previewedFile, setPreviewedFile] = useState(null)
  const [policyFiles, setPolicyFiles] = useState([])

  const setPreviewFile = useCallback(({id: fileId}) => {
    const previewedFile = fileId ? policyFiles?.find(file => file.id === fileId) : null
    setPreviewedFile(previewedFile)
  }, [policyFiles])

  const history = useHistory()
  const {t} = useTranslation()
  const {handleError} = useErrorHandling()
  const {hasAnyPrivileges} = useAuth()

  const fetchPolicies = useCallback(() => {
    fetchContractorPolicies(contractorId)
      .then(policies => {
        setPolicies(policies)
        policies.forEach(policy => {
          if(policy.insurerId !== null){
            fetchContractor(policy.insurerId).then(insurer => {
              setInsurers(previousValue => [...previousValue, {policyId: policy.id, insurerName: insurer.fullName}])
            }).catch(error => handleError(error))
          }

        })

      })
      .catch(error => handleError(error)
        .then(() => history.push('/contractors'))
      )

  }, [contractorId, handleError, history])
  useEffect(fetchPolicies, [fetchPolicies])

  useEffect(() => {
    if (insurers.length) {
      setPolicies(previousValue => previousValue.map(policy => {
        const policyId = policy.id
        const nameEntry = insurers.find(insurer => insurer.policyId === policyId)
        return nameEntry ? {...policy, insurerName: nameEntry.insurerName} : policy
      }))
    }
  }, [insurers, setPolicies]);


  const createPolicy = () => {
    history.push(`/contractors/${contractorId}/policies/create`)
  }

  const editPolicy = (policyId) => {
    history.push(`/contractors/${contractorId}/policies/edit/${policyId}`)
  }

  function formatDate(date) {
    return moment(date).format('DD-MM-YYYY')
  }

  const onDeleteDialogClose = (isDeleteConfirmed) => {
    if (isDeleteConfirmed === true) {
      deleteInsurancePolicy(elementToRemove)
        .then(fetchPolicies)
        .catch(error => handleError(error))
    }
    setElementToRemove(null)
  }

  function isAddButtonDisabled() {
    return !hasAnyPrivileges([CONTRACTOR_POLICY_CREATE])
  }

  return <>
    <WarningDialog open={!!elementToRemove}
                   onClose={onDeleteDialogClose}
                   title={'insurancePolicy.details.remove.title'}
                   content={'insurancePolicy.details.remove.content'}/>
    <FilesPreviewDialog open={!!previewedFile}
                        fileToShow={previewedFile}
                        filesSource={getInsurancePolicyFile}
                        files={policyFiles}
                        onClose={() => setPreviewedFile(null)}
                        onFileChanged={setPreviewFile}
    />
    {policies?.map(policy =>
      <div key={policy.id}
           className={classNames(styles.policyChipContainer,
             moment(policy.endDate).isAfter() ? styles.activePolicy : styles.expiredPolicy)}>
        <div>
          <InsurancePolicyTextRow label={t('insurancePolicy.form.insurerName')}
                                  text={policy.insurerName}/>
          <InsurancePolicyTextRow label={t('insurancePolicy.form.number')}
                                  text={policy.number}/>
          <InsurancePolicyTextRow label={t('insurancePolicy.details.dateRange')}
                                  text={formatDate(policy.startDate) + ' - ' + formatDate(policy.endDate)}/>
          {(policy?.files?.length > 0) &&
            <InsurancePolicyTextRow label={t('insurancePolicy.details.files')}
                                    text={`${policy?.files.length}`}/>}
        </div>
        <div className={styles.buttonsContainer}>
          {(policy?.files?.length > 0) &&
            <IconButton className={styles.icon}
                        onClick={() => {
                          setPolicyFiles(policy?.files)
                          setPreviewedFile(policy?.files[0])
                        }}>
              <PreviewIcon/>
            </IconButton>
          }
          <IconButton className={styles.icon}
                      disabled={!hasAnyPrivileges([CONTRACTOR_POLICY_EDIT])}
                      onClick={() => editPolicy(policy.id)}>
            <EditIcon/>
          </IconButton>
          <IconButton className={styles.icon}
                      disabled={!hasAnyPrivileges([CONTRACTOR_POLICY_DELETE])}
                      onClick={() => setElementToRemove(policy.id)}>
            <RemoveIcon/>
          </IconButton>
        </div>
      </div>
    )}
    <PrimaryButton
      type="button"
      label={t('insurancePolicy.details.add')}
      onClick={createPolicy}
      disabled={isAddButtonDisabled()}
      icon={<AddIcon disabled={isAddButtonDisabled()} className={styles.icon}/>}
      className={styles.addButton}
    />
  </>
}

function InsurancePolicyTextRow({label, text}) {

  return <div className={styles.textRow}>
    {label + ':'}&nbsp;
    <span className="font-weight-bold">
              {text}
            </span>
  </div>
}
